/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useId, type ReactNode } from 'react';

export interface PanelProps {
  title: ReactNode;
  /** One line under the title saying what the panel holds. */
  description?: ReactNode;
  /** Controls on the right of the head: a segmented switch, a link, a status chip. */
  actions?: ReactNode;
  /** Notes under the content (long explanations belong here, not above the content). */
  footer?: ReactNode;
  /** No padding around the content: tables and lists run edge to edge. */
  flush?: boolean;
  children?: ReactNode;
  className?: string;
  /** `h3` for a panel that is a section of a page whose own heading is an h2 (the outfit gallery's sections). */
  heading?: 'h2' | 'h3';
  /** The title's element id, when a test or a Jump to target finds the panel by it; generated otherwise. */
  titleId?: string;
}

/** A titled surface. Every block of a page is a Panel; nothing else draws a card. */
export function Panel({ title, description, actions, footer, flush = false, children, className, heading = 'h2', titleId: givenTitleId }: PanelProps) {
  const generatedId = useId();
  const titleId = givenTitleId ?? generatedId;
  const Heading = heading;
  return (
    <section className={['ui-panel', flush ? 'ui-panel--flush' : '', className ?? ''].filter(Boolean).join(' ')} aria-labelledby={titleId}>
      <div className="ui-panel-head">
        <div className="ui-panel-heading">
          <Heading className="ui-panel-title" id={titleId}>{title}</Heading>
          {description ? <p className="ui-panel-desc">{description}</p> : null}
        </div>
        {actions ? <div className="ui-panel-actions">{actions}</div> : null}
      </div>
      {children !== undefined && children !== null ? <div className="ui-panel-body">{children}</div> : null}
      {footer ? <div className="ui-panel-foot">{footer}</div> : null}
    </section>
  );
}
